import React, { useState, useRef, useEffect } from 'react';
import styles from './ChatPanel.module.css';
import { ChatMessage, ChatSuggestion, Module } from '../../types';
import { aiAssistantAPI } from '../../services/api';

// Module-specifieke suggesties en context
const MODULE_CONFIG: Record<string, { label: string; description: string; suggestions: ChatSuggestion[] }> = {
  dashboard: {
    label: 'Dashboard',
    description: 'Ik kan je helpen met projectoverzichten, KPI\'s en voortgang.',
    suggestions: [
      { id: 'dash-1', text: 'Geef een samenvatting van de projectstatus' },
      { id: 'dash-2', text: 'Welke actiepunten hebben prioriteit?' },
      { id: 'dash-3', text: 'Hoe staat het met de stakeholder betrokkenheid?' },
    ],
  },
  gebiedsanalyse: {
    label: 'Gebiedsanalyse',
    description: 'Ik kan je helpen met gebiedsdata, demografische analyse en omgevingsfactoren.',
    suggestions: [
      { id: 'geo-1', text: 'Wat zijn de belangrijkste kenmerken van dit gebied?' },
      { id: 'geo-2', text: 'Welke stakeholders zijn relevant voor deze locatie?' },
      { id: 'geo-3', text: 'Analyseer de demografische samenstelling' },
    ],
  },
  sentiment: {
    label: 'Sentiment',
    description: 'Ik kan je helpen met sentimentanalyse, trends en mediamonitoring.',
    suggestions: [
      { id: 'sent-1', text: 'Wat is het huidige sentiment rond dit project?' },
      { id: 'sent-2', text: 'Welke thema\'s genereren het meeste debat?' },
      { id: 'sent-3', text: 'Hoe kan ik negatief sentiment aanpakken?' },
    ],
  },
  personas: {
    label: 'Personas',
    description: 'Ik kan je helpen met stakeholder-persona\'s en doelgroepanalyse.',
    suggestions: [
      { id: 'pers-1', text: 'Welke persona\'s zijn het meest kritisch?' },
      { id: 'pers-2', text: 'Hoe benader ik de verschillende doelgroepen?' },
      { id: 'pers-3', text: 'Zijn er persona\'s die we missen?' },
    ],
  },
  strategy: {
    label: 'Strategie',
    description: 'Ik kan je helpen met communicatiestrategie en aanpak.',
    suggestions: [
      { id: 'strat-1', text: 'Wat zijn de kernboodschappen voor dit project?' },
      { id: 'strat-2', text: 'Welke strategie past het best bij de huidige fase?' },
      { id: 'strat-3', text: 'Hoe kan ik de strategie verbeteren?' },
    ],
  },
  media: {
    label: 'Middelen',
    description: 'Ik kan je helpen met het kiezen en genereren van communicatiemiddelen.',
    suggestions: [
      { id: 'med-1', text: 'Welke communicatiemiddelen passen bij mijn doelgroep?' },
      { id: 'med-2', text: 'Help me een nieuwsbrief opstellen' },
      { id: 'med-3', text: 'Wat is de beste kanaalstrategie?' },
    ],
  },
  werkplanning: {
    label: 'Werkplanning',
    description: 'Ik kan je helpen met planning, taken en deadlines.',
    suggestions: [
      { id: 'werk-1', text: 'Welke taken lopen achter op schema?' },
      { id: 'werk-2', text: 'Help me een planning maken voor de komende maand' },
      { id: 'werk-3', text: 'Wat zijn de eerstvolgende deadlines?' },
    ],
  },
  vragen: {
    label: 'Vragen & Antwoorden',
    description: 'Ik kan je helpen met Q&A, veelgestelde vragen en antwoorden formuleren.',
    suggestions: [
      { id: 'vr-1', text: 'Welke vragen worden het vaakst gesteld?' },
      { id: 'vr-2', text: 'Help me een antwoord formuleren op een moeilijke vraag' },
      { id: 'vr-3', text: 'Zijn er vragen die nog onbeantwoord zijn?' },
    ],
  },
  relatiebeheer: {
    label: 'Relatiebeheer',
    description: 'Ik kan je helpen met stakeholderrelaties en contactbeheer.',
    suggestions: [
      { id: 'rel-1', text: 'Met welke stakeholders moet ik contact opnemen?' },
      { id: 'rel-2', text: 'Geef een overzicht van de belangrijkste relaties' },
      { id: 'rel-3', text: 'Hoe verbeter ik de relatie met critici?' },
    ],
  },
  projectwebsite: {
    label: 'Projectwebsite',
    description: 'Ik kan je helpen met content voor de projectwebsite.',
    suggestions: [
      { id: 'web-1', text: 'Help me een tekst schrijven voor de website' },
      { id: 'web-2', text: 'Welke content mist er op de projectpagina?' },
      { id: 'web-3', text: 'Hoe maak ik de website toegankelijker?' },
    ],
  },
  'issue-radar': {
    label: 'Issue Radar',
    description: 'Ik kan je helpen met het monitoren en analyseren van issues.',
    suggestions: [
      { id: 'iss-1', text: 'Welke issues verdienen nu de meeste aandacht?' },
      { id: 'iss-2', text: 'Zijn er opkomende risico\'s die ik moet kennen?' },
      { id: 'iss-3', text: 'Hoe kan ik proactief op issues reageren?' },
    ],
  },
};

interface ChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  projectName: string;
  activeModule?: string;
}

export const ChatPanel: React.FC<ChatPanelProps> = ({
  isOpen,
  onClose,
  projectId,
  projectName,
  activeModule = 'dashboard',
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<ChatSuggestion[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const prevModuleRef = useRef(activeModule);

  // Load suggestions when panel opens or module changes
  useEffect(() => {
    if (isOpen && messages.length === 0 && projectId) {
      loadSuggestions();
    }
  }, [isOpen, projectId]);

  // Update suggestions when module changes (if no conversation yet)
  useEffect(() => {
    if (activeModule !== prevModuleRef.current) {
      prevModuleRef.current = activeModule;
      if (messages.length === 0) {
        const config = MODULE_CONFIG[activeModule];
        if (config) {
          setSuggestions(config.suggestions);
        }
      }
    }
  }, [activeModule, messages.length]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  const getModuleConfig = () => MODULE_CONFIG[activeModule] || MODULE_CONFIG.dashboard;

  const loadSuggestions = async () => {
    setLoadingSuggestions(true);
    try {
      const data = await aiAssistantAPI.getSuggestions(projectId);
      setSuggestions(data);
    } catch {
      // Gebruik module-specifieke suggesties als fallback
      setSuggestions(getModuleConfig().suggestions);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const sendMessage = async (messageText: string) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: messageText.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      // Build conversation history for the API
      const history = messages.map(m => ({
        role: m.role,
        content: m.content,
      }));

      // Voeg module-context toe zodat de AI relevante antwoorden geeft
      const config = getModuleConfig();
      const contextPrefix = `[Context: De gebruiker bevindt zich in de module "${config.label}". ${config.description}]\n\n`;
      const enrichedMessage = messages.length === 0
        ? contextPrefix + messageText.trim()
        : messageText.trim();

      const data = await aiAssistantAPI.chat(projectId, enrichedMessage, history);

      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: data.reply,
        timestamp: new Date(data.timestamp),
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error: any) {
      const errorMessage: ChatMessage = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: error.response?.data?.error || 'Er is een fout opgetreden. Probeer het opnieuw.',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const handleSuggestionClick = (suggestion: ChatSuggestion) => {
    sendMessage(suggestion.text);
  };

  const handleNewChat = () => {
    setMessages([]);
    setSuggestions([]);
    loadSuggestions();
  };

  const formatMessageContent = (content: string) => {
    // Simple markdown-like formatting
    return content
      .split('\n')
      .map((line, i) => {
        // Bold
        let formatted = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        // Headers
        if (line.startsWith('### ')) {
          return `<h4 key="${i}">${formatted.substring(4)}</h4>`;
        }
        if (line.startsWith('## ')) {
          return `<h3 key="${i}">${formatted.substring(3)}</h3>`;
        }
        // Bullet points
        if (line.startsWith('- ') || line.startsWith('* ')) {
          return `<li>${formatted.substring(2)}</li>`;
        }
        // Numbered list
        if (/^\d+\.\s/.test(line)) {
          return `<li>${formatted.replace(/^\d+\.\s/, '')}</li>`;
        }
        // Empty line = paragraph break
        if (line.trim() === '') {
          return '<br/>';
        }
        return `<p>${formatted}</p>`;
      })
      .join('');
  };

  if (!isOpen) return null;

  return (
    <>
      <div className={styles.overlay} onClick={onClose} />
      <div className={styles.panel}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerInfo}>
            <div className={styles.headerIcon}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            </div>
            <div>
              <h3 className={styles.headerTitle}>AI Assistent</h3>
              <span className={styles.headerSubtitle}>{projectName} · {getModuleConfig().label}</span>
            </div>
          </div>
          <div className={styles.headerActions}>
            {messages.length > 0 && (
              <button
                className={styles.newChatButton}
                onClick={handleNewChat}
                title="Nieuw gesprek"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </button>
            )}
            <button className={styles.closeButton} onClick={onClose} title="Sluiten">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Messages area */}
        <div className={styles.messagesContainer}>
          {messages.length === 0 && !isLoading ? (
            <div className={styles.welcome}>
              <div className={styles.welcomeIcon}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
              </div>
              <h4 className={styles.welcomeTitle}>Hallo! Hoe kan ik helpen?</h4>
              <p className={styles.welcomeText}>
                {getModuleConfig().description}
              </p>

              {/* Suggestions */}
              {!loadingSuggestions && suggestions.length > 0 && (
                <div className={styles.suggestions}>
                  {suggestions.map(suggestion => (
                    <button
                      key={suggestion.id}
                      className={styles.suggestionButton}
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                      {suggestion.text}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className={styles.messagesList}>
              {messages.map(message => (
                <div
                  key={message.id}
                  className={`${styles.message} ${
                    message.role === 'user' ? styles.userMessage : styles.assistantMessage
                  }`}
                >
                  {message.role === 'assistant' && (
                    <div className={styles.messageAvatar}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                      </svg>
                    </div>
                  )}
                  <div className={styles.messageBubble}>
                    {message.role === 'assistant' ? (
                      <div
                        className={styles.messageContent}
                        dangerouslySetInnerHTML={{
                          __html: formatMessageContent(message.content),
                        }}
                      />
                    ) : (
                      <div className={styles.messageContent}>{message.content}</div>
                    )}
                  </div>
                </div>
              ))}

              {/* Loading indicator */}
              {isLoading && (
                <div className={`${styles.message} ${styles.assistantMessage}`}>
                  <div className={styles.messageAvatar}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                  </div>
                  <div className={styles.messageBubble}>
                    <div className={styles.typingIndicator}>
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input area */}
        <form className={styles.inputArea} onSubmit={handleSubmit}>
          <div className={styles.inputWrapper}>
            <textarea
              ref={inputRef}
              className={styles.input}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Stel een vraag..."
              rows={1}
              disabled={isLoading}
            />
            <button
              type="submit"
              className={styles.sendButton}
              disabled={!input.trim() || isLoading}
              title="Verstuur"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
        </form>
      </div>
    </>
  );
};
